/**
 * SpatialSystem — OWNER: spatial builder (packages/sim/src/spatial/**).
 * Walkable geometry and nav grid from DungeonDef (cached per dungeon), A* + smoothing,
 * movement integration with circle-vs-wall collision, range/arc math and shape tests.
 */
import type { ContentRegistry, SimContext, SpatialSystem, Unit, UnitId, UnitQuery, Vec2, World, WorldShape } from '../contracts';
import { MELEE_RANGE_BONUS, MIN_MELEE_RANGE, MOVING_LEEWAY, NPC_WALK_FRACTION } from '../contracts';
import { angleTo, backpedalFactor, dist, shapeContains, wrapAngle } from './geometry';
import { initialDoorStates, navWithDoors, updateDoors } from './doors';
import { afterGroundMove, groundUnit, perchCollide, stepAir } from './air'; // +glide
import { WALL_RADIUS, buildNav, circleWalkable, clampToWalkable, findPath, isWalkable, lineOfSight, reachMask, roomAt, type NavData } from './nav';

export { shapeContains, wrapAngle, angleTo, backpedalFactor } from './geometry';
export { doorPolygon } from './doors';

/** How far nearestSafePoint searches, and its ring/angle resolution. */
const SAFE_SEARCH_MAX: number = 40;
const SAFE_SEARCH_STEP = 0.5;
const SAFE_SEARCH_ANGLES = 24;
const EPS = 1e-6;

export function createSpatialSystem(content: ContentRegistry): SpatialSystem {
  const navCache = new Map<string, NavData>();
  /** Static nav (no doors closed). */
  const baseNav = (w: World): NavData => {
    let n = navCache.get(w.dungeonId);
    if (!n) {
      n = buildNav(content.dungeon(w.dungeonId));
      navCache.set(w.dungeonId, n);
    }
    return n;
  };
  /** Nav with the world's closed doors as blockers (cached per closed-door set). */
  const nav = (w: World): NavData => (w.doors ? navWithDoors(baseNav(w), content.dungeon(w.dungeonId), w) : baseNav(w));

  const leeway = (a: Unit, b: Unit): number => (a.moving && b.moving ? MOVING_LEEWAY : 0);
  const pair = (w: World, aId: UnitId, bId: UnitId): [Unit, Unit] | null => {
    const a = w.units[aId];
    const b = w.units[bId];
    return a && b ? [a, b] : null;
  };

  /** Where a unit ends up trying to go from→to: full move, else slide along one axis, else stay. */
  let airCtx: SimContext | null = null; // +glide: set during moveUnits (perched units collide in the air)
  const collide = (n: NavData, u: Unit, from: Vec2, to: Vec2): Vec2 => {
    if (u.perch && airCtx) return perchCollide(n, airCtx, u, from, to); // +glide
    const r = Math.min(u.radius, WALL_RADIUS);
    if (circleWalkable(n, to, r)) return to;
    // Already embedded in a wall (spawned badly, knocked into one): allow anything walkable.
    if (!circleWalkable(n, from, r) && isWalkable(n, to)) return to;
    const sx = { x: to.x, y: from.y };
    if (Math.abs(to.x - from.x) > EPS && circleWalkable(n, sx, r)) return sx;
    const sy = { x: from.x, y: to.y };
    if (Math.abs(to.y - from.y) > EPS && circleWalkable(n, sy, r)) return sy;
    return from;
  };

  const towards = (from: Vec2, to: Vec2, step: number): Vec2 => {
    const d = dist(from, to);
    if (d <= step) return { x: to.x, y: to.y };
    return { x: from.x + ((to.x - from.x) / d) * step, y: from.y + ((to.y - from.y) / d) * step };
  };

  function moveUnit(ctx: SimContext, n: NavData, u: Unit, dtMs: number): void {
    const move = u.move;
    if (!move) return;
    if (move.mode === 'forced') {
      if (move.speed <= 0) {
        u.pos = clampToWalkable(n, move.to);
      } else if (dist(u.pos, move.to) > EPS) {
        const next = collide(n, u, u.pos, towards(u.pos, move.to, (move.speed * dtMs) / 1000));
        if (dist(next, u.pos) <= EPS) move.to = { ...u.pos }; // hit a wall: displacement ends here
        u.pos = next;
      }
      // The intent (and its "cannot act" meaning) lasts until `until`, even if we arrived early.
      if (ctx.now >= move.until) u.move = null;
      return;
    }
    if (!ctx.sys.combat.controlState(ctx, u.id).canMove) return;
    const speed = ctx.sys.combat.effectiveStats(ctx, u.id).moveSpeed * (move.mode === 'dest' && move.walk ? NPC_WALK_FRACTION : 1);
    if (speed <= 0) return;
    let budget = (speed * dtMs) / 1000;

    if (move.mode === 'dir') {
      const len = Math.hypot(move.dir.x, move.dir.y);
      if (len <= EPS) return;
      // Backpedal: a human moving against its facing goes at 4.5/7 speed (bots are unaffected).
      budget *= backpedalFactor(u.controller, move.dir, u.facing);
      u.pos = collide(n, u, u.pos, { x: u.pos.x + (move.dir.x / len) * budget, y: u.pos.y + (move.dir.y / len) * budget });
      return;
    }

    // 'dest': follow waypoints, spending the whole step budget across several if needed.
    let repathed = false;
    while (budget > EPS && move.pathIndex < move.path.length) {
      const wp = move.path[move.pathIndex]!;
      const d = dist(u.pos, wp);
      if (d <= EPS) {
        move.pathIndex += 1;
        continue;
      }
      u.facing = angleTo(u.pos, wp);
      const want = towards(u.pos, wp, budget);
      const got = collide(n, u, u.pos, want);
      const moved = dist(u.pos, got);
      u.pos = got;
      if (moved <= EPS) {
        // Blocked: re-path once, else give up.
        const path = repathed ? null : findPath(n, u.pos, move.dest);
        if (!path) {
          u.move = null;
          return;
        }
        repathed = true;
        move.path = path;
        move.pathIndex = 0;
        continue;
      }
      if (dist(got, want) > EPS) break; // slid along a wall; done for this tick
      budget -= moved;
      if (moved >= d - EPS) move.pathIndex += 1;
    }
    if (move.pathIndex >= move.path.length) u.move = null;
  }

  const matches = (w: World, u: Unit, q: UnitQuery): boolean => {
    if ((q.aliveOnly ?? true) && !u.alive) return false;
    if (q.excludeIds?.includes(u.id)) return false;
    const rel = q.relativeTo ? w.units[q.relativeTo] : undefined;
    if (!rel || !q.affects || q.affects === 'all') return true;
    switch (q.affects) {
      case 'enemies':
        return u.faction !== rel.faction;
      case 'allies':
        return u.faction === rel.faction;
      case 'allies_not_self':
        return u.faction === rel.faction && u.id !== rel.id;
    }
  };

  const sys: SpatialSystem = {
    init(ctx) {
      const dungeon = content.dungeon(ctx.world.dungeonId);
      if (dungeon?.doors?.length && !ctx.world.doors) ctx.world.doors = initialDoorStates(dungeon);
      nav(ctx.world);
      if (dungeon?.airspace) reachMask(baseNav(ctx.world), dungeon.entrance); // +glide: the walkers' ground, flood-filled once at load
    },

    moveUnits(ctx, dtMs) {
      updateDoors(ctx, content.dungeon(ctx.world.dungeonId), baseNav(ctx.world));
      const n = nav(ctx.world);
      for (const id of ctx.world.unitOrder) {
        const u = ctx.world.units[id];
        if (!u) continue;
        u.moving = false;
        if (!u.alive) {
          if (u.perch || (u.jump?.air && !u.jump.air.landed)) groundUnit(ctx, n, u); // +glide: the fallen come down
          u.move = null;
          continue;
        }
        const before = u.pos;
        // +glide: free flight has its own step; a hop / a platform walk checks platforms and updrafts after.
        if (u.jump?.air) stepAir(ctx, n, u, dtMs);
        else {
          airCtx = ctx;
          moveUnit(ctx, n, u, dtMs);
          airCtx = null;
          if (u.perch || u.jump) afterGroundMove(ctx, n, u, dtMs, before);
        }
        u.moving = dist(before, u.pos) > EPS;
      }
    },

    setDestination(ctx, unitId, dest) {
      const u = ctx.world.units[unitId];
      if (!u) return false;
      const path = findPath(nav(ctx.world), u.pos, dest);
      if (!path) return false;
      const end = path[path.length - 1];
      u.move = end ? { mode: 'dest', dest: { ...end }, path, pathIndex: 0 } : null;
      return true;
    },

    findPath: (w, from, to) => findPath(nav(w), from, to),
    lineOfSight: (w, a, b) => lineOfSight(nav(w), a, b),
    isWalkable: (w, p) => isWalkable(nav(w), p),
    clampToWalkable: (w, p) => clampToWalkable(nav(w), p),
    roomAt: (w, p) => roomAt(nav(w), p),
    distance: dist,

    rangeBetween(w, aId, bId) {
      const p = pair(w, aId, bId);
      if (!p) return Infinity;
      return Math.max(0, dist(p[0].pos, p[1].pos) - p[0].combatReach - p[1].combatReach);
    },

    inMeleeRange(w, aId, bId) {
      const p = pair(w, aId, bId);
      if (!p) return false;
      const [a, b] = p;
      const reach = Math.max(MIN_MELEE_RANGE, a.combatReach + b.combatReach + MELEE_RANGE_BONUS);
      return dist(a.pos, b.pos) <= reach + leeway(a, b);
    },

    inRange(w, aId, bId, range) {
      if (range <= 0) return sys.inMeleeRange(w, aId, bId);
      const p = pair(w, aId, bId);
      if (!p) return false;
      return sys.rangeBetween(w, aId, bId) <= range + leeway(p[0], p[1]);
    },

    isInFront(w, aId, bId, arc) {
      const p = pair(w, aId, bId);
      if (!p) return false;
      const [a, b] = p;
      if (dist(a.pos, b.pos) <= EPS) return true;
      return Math.abs(wrapAngle(angleTo(a.pos, b.pos) - a.facing)) <= arc / 2 + EPS;
    },

    isBehind(w, aId, bId) {
      const p = pair(w, aId, bId);
      if (!p || dist(p[0].pos, p[1].pos) <= EPS) return false;
      return !sys.isInFront(w, bId, aId, Math.PI);
    },

    resolveArea(w, shape, anchor, casterId, targetId, point): WorldShape {
      const c = w.units[casterId];
      const t = targetId ? w.units[targetId] : undefined;
      const casterPos = c?.pos ?? point ?? t?.pos ?? { x: 0, y: 0 };
      const aim = point ?? t?.pos ?? null;
      const origin = anchor === 'point' ? (point ?? t?.pos ?? casterPos) : anchor === 'target' ? (t?.pos ?? point ?? casterPos) : casterPos;
      const facing = aim && dist(casterPos, aim) > EPS ? angleTo(casterPos, aim) : (c?.facing ?? 0);
      return { shape, origin: { x: origin.x, y: origin.y }, facing };
    },

    shapeContains,

    unitsInShape(w, area, q) {
      const hits: { id: UnitId; d: number }[] = [];
      for (const id of w.unitOrder) {
        const u = w.units[id];
        if (!u || !matches(w, u, q)) continue;
        if (shapeContains(area, u.pos, u.radius)) hits.push({ id, d: dist(area.origin, u.pos) });
      }
      hits.sort((a, b) => a.d - b.d || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
      return hits.map((h) => h.id);
    },

    unitsInRadius(w, center, radius, q) {
      return sys.unitsInShape(w, { shape: { type: 'circle', radius }, origin: center, facing: 0 }, q);
    },

    nearestSafePoint(w, from, danger, radius) {
      const n = nav(w);
      const safe = (p: Vec2): boolean => circleWalkable(n, p, Math.min(radius, WALL_RADIUS)) && danger.every((d) => !shapeContains(d, p, radius));
      if (safe(from)) return { x: from.x, y: from.y };
      for (let r = SAFE_SEARCH_STEP; r <= SAFE_SEARCH_MAX; r += SAFE_SEARCH_STEP) {
        for (let i = 0; i < SAFE_SEARCH_ANGLES; i++) {
          const a = (i * 2 * Math.PI) / SAFE_SEARCH_ANGLES;
          const p = { x: from.x + Math.cos(a) * r, y: from.y + Math.sin(a) * r };
          if (safe(p)) return p;
        }
      }
      return null;
    },
  };
  return sys;
}
